// Card "Cota real" do Dashboard — pedido CORRECAO_EMERGENT_COTA_REAL_SIMPLIFICAR.
// Visível apenas para Gerente, Diretor e Diretor Geral. Vendedor e Admin NÃO veem.
// Mostra anel com % faturado/cota real do mês, faturado × cota real do mês,
// e quanto falta. Faturado é o mesmo exibido no card META · EQUIPE.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import api from "@/lib/api";
import { brl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Target } from "@/components/icons";

const MESES_LONG = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

export default function CotaRealCard({ faturadoMes }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  // Vendedor e Admin não veem — pedido CORRECAO_EMERGENT_COTA_REAL_SIMPLIFICAR item 4.
  // Lote 3 CRM-006: Diretor Geral também sem cota real própria (oculto).
  const hidden = role === "seller" || role === "admin" || role === "diretor_geral";
  const [cotaMes, setCotaMes] = useState(null);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const ano = now.getFullYear();
  const mesIdx = now.getMonth();

  useEffect(() => {
    if (hidden || !user) return;
    (async () => {
      try {
        if (role === "manager" || role === "director") {
          const r = await api.get(`/cotas-oficiais/${user.id}?ano=${ano}`);
          const meses = r.data?.meses || Array(12).fill(0);
          setCotaMes(Number(meses[mesIdx] || 0));
        } else if (role === "diretor_geral") {
          const r = await api.get(`/cotas-oficiais?ano=${ano}`);
          const items = r.data?.items || [];
          const soma = items.reduce((s, it) => s + Number((it.meses || [])[mesIdx] || 0), 0);
          setCotaMes(soma);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [hidden, user, role, ano, mesIdx]);

  if (hidden) return null;

  const faturado = Number(faturadoMes || 0);
  const cota = Number(cotaMes || 0);
  const pct = cota > 0 ? Math.round((faturado / cota) * 100) : 0;
  const faltam = Math.max(0, cota - faturado);
  const batida = cota > 0 && faturado >= cota;
  const mesNome = MESES_LONG[mesIdx].toUpperCase();

  // Sem cota cadastrada no mês → convite para cadastrar.
  // Lote 3 CRM-006: Administrador e Gerente editam cota real. Diretor
  // também (LOTE PERM-1.1). LOTE UX-2B item 6: o cartão inteiro vira
  // link quando o usuário pode cadastrar.
  if (!loading && cota <= 0) {
    const canCadastrar = role === "manager" || role === "director" || role === "admin";
    const goto = () => navigate("/equipe/cotas-lote?aba=real");
    return (
      <section
        className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]"
        data-testid="cota-real-card"
        role={canCadastrar ? "button" : undefined}
        tabIndex={canCadastrar ? 0 : -1}
        onClick={canCadastrar ? goto : undefined}
        onKeyDown={canCadastrar ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goto(); } } : undefined}
        style={canCadastrar ? { cursor: "pointer" } : undefined}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#F3F8FB" }}>
            <Target className="w-5 h-5" style={{ color: "#008AB0" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Cota real · {MESES_LONG[mesIdx]}
            </div>
            <div className="text-sm text-navy mt-0.5">
              {canCadastrar ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); goto(); }}
                  className="font-semibold text-brand hover:text-brand-hover underline-offset-2 hover:underline"
                  data-testid="cota-real-cadastrar"
                >
                  Cadastre sua cota real
                </button>
              ) : (
                <span className="text-muted-foreground">Cota real não cadastrada</span>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  const ring = [
    { value: Math.min(100, pct) },
    { value: Math.max(0, 100 - Math.min(100, pct)) },
  ];

  return (
    <section
      className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]"
      data-testid="cota-real-card"
    >
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20 shrink-0" aria-hidden>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={ring}
                dataKey="value"
                innerRadius={28}
                outerRadius={38}
                startAngle={90}
                endAngle={-270}
                stroke="none"
                isAnimationActive={false}
              >
                <Cell fill="#008AB0" />
                <Cell fill="#E6EEF4" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-lg font-extrabold text-navy tabular-nums" data-testid="cota-real-pct">{pct}%</span>
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground">da cota</span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Cota real · {mesNome}
          </div>
          <div className="mt-0.5 text-lg font-bold text-navy tabular-nums leading-tight">
            <span data-testid="cota-real-faturado">{brl(faturado)}</span>
            <span className="text-sm font-normal text-muted-foreground"> de </span>
            <span data-testid="cota-real-cota">{brl(cota)}</span>
          </div>
          <div className="mt-1 text-xs">
            {batida ? (
              <span className="font-semibold text-ok" data-testid="cota-real-status">Cota batida</span>
            ) : (
              <span className="text-navy" data-testid="cota-real-status">
                Faltam <b className="tabular-nums">{brl(faltam)}</b>
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
